import React, {useEffect, useMemo, useState, Suspense, lazy} from "react";
import {Routes, Route} from "react-router-dom";
import Header from "../components/header/Header";
import ScrollToHash from "@app/ScrollToHash";
import ScrollToTopButton from "./topbutton/Top";
import ErrorBoundary from "@app/ErrorBoundary";
import {splashScreen} from "@features/portfolio/config";
import {StyleProvider} from "../contexts/StyleContext";
import {useSettings} from "../contexts/SettingsContext";
import {useLocalStorage} from "@shared/hooks/useLocalStorage";
import Loader from "../components/loader/Loader";
import "./Main.scss";

// ---- Route-level code splitting ----
// Each entry below is its own webpack chunk. Public visitors no longer
// download the admin/editor or markdown/syntax-highlighter bundles up front.
const BlogList       = lazy(() => import("./blog/BlogList"));
const BlogDetail     = lazy(() => import("./blog/BlogDetail"));
const Resume         = lazy(() => import("./resume/Resume"));
const AdminLogin     = lazy(() => import("./admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const SplashScreen   = lazy(() => import("./splashScreen/SplashScreen"));

// Portfolio composes several heavy sections. Each section is itself lazy so
// the /portfolio chunk doesn't balloon to include every container.
const Greeting        = lazy(() => import("./greeting/Greeting"));
const Skills          = lazy(() => import("./skills/Skills"));
const StackProgress   = lazy(() => import("./skillProgress/skillProgress"));
const WorkExperience  = lazy(() => import("./workExperience/WorkExperience"));
const Education       = lazy(() => import("./education/Education"));
const Profile         = lazy(() => import("./profile/Profile"));
const StartupProject  = lazy(() => import("./StartupProjects/StartupProject"));

const Portfolio = () => {
  const {isVisible, loaded} = useSettings();
  if (!loaded) return <Loader label="Rashid Ahmad" />;
  return (
    <>
      {isVisible("sections.greeting")   && <Greeting />}
      {isVisible("sections.skills")     && <Skills />}
      {isVisible("sections.techStack")  && <StackProgress />}
      {isVisible("sections.education")  && <Education />}
      {isVisible("sections.experience") && <WorkExperience />}
      {isVisible("sections.projects")   && <StartupProject />}
      {isVisible("sections.contact")    && <Profile />}
    </>
  );
};

const RouteFallback = () => <Loader label="Loading" />;

// Per-route ErrorBoundary + Suspense wrapper. Isolating each route means a
// crash on /admin can never blank out / or /blog, and a slow chunk only stalls
// its own subtree.
const RouteShell = ({children}) => (
  <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>{children}</Suspense>
  </ErrorBoundary>
);

const Main = () => {
  const darkPref = window.matchMedia("(prefers-color-scheme: dark)");
  const [isDark, setIsDark] = useLocalStorage("isDark", darkPref.matches);
  const [isShowingSplashAnimation, setIsShowingSplashAnimation] =
    useState(true);

  useEffect(() => {
    if (splashScreen.enabled) {
      const splashTimer = setTimeout(
        () => setIsShowingSplashAnimation(false),
        splashScreen.duration
      );
      return () => {
        clearTimeout(splashTimer);
      };
    }
  }, []);

  // Stable identity for the StyleContext value — without useMemo, every Main
  // re-render produces a brand-new {isDark, changeTheme} object and every
  // consumer re-renders even when isDark is unchanged.
  const styleValue = useMemo(
    () => ({isDark, changeTheme: () => setIsDark(d => !d)}),
    [isDark, setIsDark]
  );

  return (
    <div className={isDark ? "dark-mode" : null}>
      <StyleProvider value={styleValue}>
        {isShowingSplashAnimation && splashScreen.enabled ? (
          <Suspense fallback={<RouteFallback />}>
            <SplashScreen />
          </Suspense>
        ) : (
          <>
            <Header />
            <ScrollToHash />
            <Routes>
              <Route path="/"             element={<RouteShell><BlogList /></RouteShell>} />
              <Route path="/blog"         element={<RouteShell><BlogList /></RouteShell>} />
              <Route path="/blog/:id"     element={<RouteShell><BlogDetail /></RouteShell>} />
              <Route path="/portfolio"    element={<RouteShell><Portfolio /></RouteShell>} />
              <Route path="/resume"       element={<RouteShell><Resume /></RouteShell>} />
              <Route path="/admin/login"  element={<RouteShell><AdminLogin /></RouteShell>} />
              <Route path="/admin"        element={<RouteShell><AdminDashboard /></RouteShell>} />
            </Routes>
            <ScrollToTopButton />
          </>
        )}
      </StyleProvider>
    </div>
  );
};

export default Main;
